import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Box, Text, HStack, VStack, Input, Button, Select, Checkbox, FormControl, FormLabel, Portal } from '@chakra-ui/react';
import { LuType, LuSparkles, LuChevronDown, LuEraser } from 'react-icons/lu';

const AVAILABLE_VARIABLES = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'position', label: 'Position' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'location', label: 'Location' },
  { key: 'email', label: 'Email' },
  { key: 'company_industry', label: 'Industry' },
  { key: 'company_website', label: 'Website' },
];

const chipStyles = `
  @keyframes polish-shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position: 600px 0; }
  }
  .polish-shimmer-line {
    border-radius: 6px;
    height: 11px;
    background: linear-gradient(
      90deg,
      rgba(255,255,255,0.04) 0%,
      rgba(255,255,255,0.10) 40%,
      rgba(255,255,255,0.04) 100%
    );
    background-size: 1200px 100%;
    animation: polish-shimmer 1.6s ease-in-out infinite;
  }

  .variable-chip {
    display: inline-flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 4px;
    padding: 0 8px;
    margin: 0 2px;
    color: white;
    vertical-align: middle;
    user-select: none;
    font-weight: 400;
    height: 24px;
  }
  .chip-icon { color: #7a85ff; margin-right: 6px; display: flex; align-items: center; }
  .chip-close { 
    margin-right: 8px; 
    cursor: pointer; 
    color: #888; 
    font-size: 14px;
    display: flex;
    align-items: center;
  }
  .chip-close:hover { color: #ff5a5a; }
  .chip-text { font-size: 13px; display: flex; align-items: center; }
  .chip-nested-label {
    background: rgba(122, 133, 255, 0.2);
    padding: 0 4px;
    border-radius: 2px;
    color: #a5adff;
    margin: 0 4px;
  }

  .ai-prompt-chip {
    display: inline-flex;
    align-items: flex-start;
    cursor: text;
    background: rgba(134, 239, 172, 0.22);
    border: 1px solid rgba(74, 222, 128, 0.5);
    border-radius: 6px;
    padding: 2px 6px 2px 4px;
    margin: 0 2px;
    vertical-align: top;
    user-select: none;
    max-width: 100%;
    box-sizing: border-box;
  }
  .ai-prompt-chip-icon {
    color: #4ade80;
    margin-right: 6px;
    margin-top: 2px;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }
  .ai-prompt-chip-svg { display: block; }
  .ai-prompt-chip-text {
    font-size: 13px;
    font-weight: 400;
    color: #ecfdf5;
    white-space: normal;
    word-break: break-word;
    flex: 1;
    min-width: 0;
    line-height: 1.35;
  }
  .ai-prompt-chip-placeholder {
    color: rgba(167, 243, 208, 0.85);
    font-style: italic;
  }
  .ai-prompt-chip-close {
    margin-left: 6px;
    margin-top: 2px;
    cursor: pointer;
    color: rgba(134, 239, 172, 0.65);
    font-size: 14px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }
  .ai-prompt-chip-close:hover { color: #f87171; }
`;

const escapeHtml = (s) => {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

/** Same sparkle + pencil motif as PolishIcon, inlined for contenteditable HTML. */
/**
 * Zero-width space after non-editable chips so the browser has a real text offset to type at.
 * Stripped in htmlToValue so it never persists in the saved draft.
 */
const EDITOR_CARET_ZWSP = '\u200B';

const AI_PROMPT_CHIP_ICON_HTML =
  '<span class="ai-prompt-chip-icon">' +
  '<svg class="ai-prompt-chip-svg" width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
  '<path d="M4.5 2L4.5 4.8L8.5 5.5L5.5 6.2L4.5 9L3.5 6.2L0.5 5.5L3.5 4.8Z" fill="currentColor"/>' +
  '<path d="M9 17L15.5 10.5L17.5 12.5L11 19H9V17Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>' +
  '<path d="M15.5 10.5L17 8.5L19.5 11L17.5 12.5Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg></span>';

function varKeyToChipHtml(content) {
  if (content === 'CLEAN_PENDING') {
    return (
      `<span contenteditable="false" data-variable="${content}" class="variable-chip">` +
      `<span class="chip-close" data-remove="${content}">×</span>` +
      `<span class="chip-icon"><svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" height="14" width="14" xmlns="http://www.w3.org/2000/svg"><path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.9-9.9c1-1 2.5-1 3.4 0l4.3 4.3c1 1 1 2.5 0 3.4L10.5 21z"></path><path d="m14 7 3 3"></path><path d="M5 11 11 5"></path></svg></span>` +
      `<span class="chip-text">"Clean variable"</span></span>`
    );
  }
  if (content.startsWith('CLEAN|')) {
    const [, column, , , fallback] = content.split('|');
    return (
      `<span contenteditable="false" data-variable="${content}" class="variable-chip">` +
      `<span class="chip-close" data-remove="${content}">×</span>` +
      `<span class="chip-icon"><svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" height="14" width="14" xmlns="http://www.w3.org/2000/svg"><path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.9-9.9c1-1 2.5-1 3.4 0l4.3 4.3c1 1 1 2.5 0 3.4L10.5 21z"></path><path d="m14 7 3 3"></path><path d="M5 11 11 5"></path></svg></span>` +
      `<span class="chip-text"><span class="chip-nested-label">${column || 'field'}</span> or '${escapeHtml(fallback)}'</span></span>`
    );
  }
  const varDef = AVAILABLE_VARIABLES.find((v) => v.key === content);
  const label = varDef?.label || content;
  return (
    `<span contenteditable="false" data-variable="${content}" class="variable-chip">` +
    `<span class="chip-icon">T</span><span class="chip-text">${escapeHtml(label)}</span><span class="chip-close" style="margin-right: 0; margin-left: 6px;" data-remove="${content}">×</span></span>`
  );
}

/**
 * Next index of `//` that starts an AI delimiter, not the slashes in `http://` / `https://`.
 */
function indexOfPromptDelimiter(str, fromIndex = 0) {
  let i = fromIndex;
  while (i < str.length) {
    const j = str.indexOf('//', i);
    if (j === -1) return -1;
    if (j > 0 && str[j - 1] === ':') {
      i = j + 2;
      continue;
    }
    return j;
  }
  return -1;
}

/** True if the model uses AI `//` delimiters (ignores `://` in URLs). */
function modelHasAiDelimiter(str) {
  return typeof str === 'string' && indexOfPromptDelimiter(str, 0) !== -1;
}

/**
 * Split draft text into plain text, {{variable}}, and // AI prompt // segments.
 * Unclosed // starts an open AI segment through end of string (matches backend DOTALL intent).
 */
function parseDraftSegments(str, includeAi) {
  const segs = [];
  let i = 0;
  while (i < str.length) {
    const iVar = str.indexOf('{{', i);
    const iAi = includeAi ? indexOfPromptDelimiter(str, i) : -1;
    let nextIdx = Infinity;
    let kind = null;
    if (iVar !== -1 && iVar < nextIdx) {
      nextIdx = iVar;
      kind = 'var';
    }
    if (iAi !== -1 && iAi < nextIdx) {
      nextIdx = iAi;
      kind = 'ai';
    }
    if (kind === null) {
      segs.push({ type: 'text', content: str.slice(i) });
      break;
    }
    if (nextIdx > i) {
      segs.push({ type: 'text', content: str.slice(i, nextIdx) });
    }
    if (kind === 'var') {
      const end = str.indexOf('}}', nextIdx + 2);
      if (end === -1) {
        segs.push({ type: 'text', content: str.slice(nextIdx) });
        break;
      }
      segs.push({ type: 'var', content: str.slice(nextIdx + 2, end) });
      i = end + 2;
      continue;
    }
    if (kind === 'ai') {
      const after = nextIdx + 2;
      const close = indexOfPromptDelimiter(str, after);
      if (close === -1) {
        segs.push({ type: 'ai', content: str.slice(after), closed: false });
        break;
      }
      segs.push({ type: 'ai', content: str.slice(after, close), closed: true });
      i = close + 2;
      continue;
    }
  }
  return segs;
}

/** Closed //â€¦// segments only; open segments render as plain text in valueToHtml. */
function closedAiPromptChipHtml(inner) {
  const enc = encodeURIComponent(inner);
  const hasText = (inner || '').trim().length > 0;
  // Use explicit <br> (same as open segments) so innerHTML matches valueToHtml output after
  // chipify â€” raw newlines inside the span normalize differently in browsers and caused
  // innerHTML !== nextHtml on every input, nuking the caret for multi-line prompts.
  const displayInner = hasText
    ? escapeHtml(inner).replace(/\n/g, '<br>')
    : '<span class="ai-prompt-chip-placeholder">empty</span>';
  return (
    `<span contenteditable="false" class="ai-prompt-chip" data-ai-inner="${enc}" data-ai-closed="1">` +
    AI_PROMPT_CHIP_ICON_HTML +
    `<span class="ai-prompt-chip-text">${displayInner}</span>` +
    '<span class="ai-prompt-chip-close" data-ai-remove="1">×</span>' +
    `</span>${EDITOR_CARET_ZWSP}`
  );
}

/** Model length of a text node (ZWSP sentinels do not count). */
function textNodeModelLength(raw) {
  return (raw || '').replace(/\u200B/g, '').length;
}

/**
 * Map a model offset inside this text node to a DOM offset (ZWSP skipped for index math).
 * `modelIndexWithinNode` is 0..modelLen inclusive (inclusive end = caret after last char).
 */
function rawOffsetForModelIndexInTextNode(raw, modelIndexWithinNode) {
  const s = raw || '';
  if (modelIndexWithinNode <= 0) return 0;
  let m = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === EDITOR_CARET_ZWSP) continue;
    m += 1;
    if (m === modelIndexWithinNode) return i + 1;
  }
  return s.length;
}

const EDITOR_BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'BLOCKQUOTE', 'PRE']);

function editorElementToModelToken(el) {
  if (!el?.classList) return null;
  if (el.classList.contains('variable-chip')) {
    const k = el.getAttribute('data-variable');
    return k ? `{{${k}}}` : null;
  }
  if (el.classList.contains('ai-prompt-chip')) {
    const enc = el.getAttribute('data-ai-inner') || '';
    let inner = '';
    try {
      inner = decodeURIComponent(enc);
    } catch {
      inner = '';
    }
    const closed = el.getAttribute('data-ai-closed') === '1';
    return `//${inner}${closed ? '//' : ''}`;
  }
  return null;
}

/** Model-string offset of the caret; must stay aligned with htmlToValue(editor.innerHTML). */
function getModelOffsetBeforeCaret(root, range, htmlToValue) {
  if (!root || !range || !root.contains(range.startContainer)) return 0;
  try {
    const pre = range.cloneRange();
    pre.selectNodeContents(root);
    pre.setEnd(range.startContainer, range.startOffset);
    const wrap = document.createElement('div');
    wrap.appendChild(pre.cloneContents());
    return htmlToValue(wrap.innerHTML).length;
  } catch {
    return 0;
  }
}

function applySelectionRange(range) {
  const sel = window.getSelection();
  if (!sel) return;
  sel.removeAllRanges();
  sel.addRange(range);
}

/** Prefer caret inside the ZWSP text node after a chip (stable typing); else after the element. */
function placeCaretAfterNonEditableChip(chipEl) {
  const next = chipEl.nextSibling;
  if (next && next.nodeType === Node.TEXT_NODE) {
    const nv = next.nodeValue || '';
    const zi = nv.indexOf(EDITOR_CARET_ZWSP);
    if (zi !== -1) {
      const r = document.createRange();
      r.setStart(next, zi);
      r.collapse(true);
      applySelectionRange(r);
      return;
    }
  }
  const r = document.createRange();
  r.setStartAfter(chipEl);
  r.collapse(true);
  applySelectionRange(r);
}

/**
 * Place collapsed caret after `targetOffset` model chars (same indexing as htmlToValue on root).
 */
function setCaretToModelOffset(root, targetOffset, htmlToValue) {
  if (!root) return;
  const max = htmlToValue(root.innerHTML).length;
  const tgt = Math.min(Math.max(0, targetOffset), max);
  let pos = 0;

  const walk = (node, isRoot) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.nodeValue || '';
      const mlen = textNodeModelLength(raw);
      if (mlen > 0 && tgt >= pos && tgt <= pos + mlen) {
        const local = tgt - pos;
        const off = rawOffsetForModelIndexInTextNode(raw, local);
        const r = document.createRange();
        r.setStart(node, off);
        r.collapse(true);
        applySelectionRange(r);
        return true;
      }
      if (mlen === 0 && raw.length > 0 && tgt === pos) {
        const r = document.createRange();
        r.setStart(node, 0);
        r.collapse(true);
        applySelectionRange(r);
        return true;
      }
      pos += mlen;
      return false;
    }
    if (node.nodeName === 'BR') {
      if (tgt === pos) {
        const r = document.createRange();
        r.setStartBefore(node);
        r.collapse(true);
        applySelectionRange(r);
        return true;
      }
      pos += 1;
      return false;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tok = editorElementToModelToken(node);
      if (tok !== null) {
        const len = tok.length;
        if (tgt <= pos) {
          const r = document.createRange();
          r.setStartBefore(node);
          r.collapse(true);
          applySelectionRange(r);
          return true;
        }
        if (tgt <= pos + len) {
          placeCaretAfterNonEditableChip(node);
          return true;
        }
        pos += len;
        return false;
      }
    }

    const childNodes = Array.from(node.childNodes);
    const isBlock = !isRoot && node.nodeType === Node.ELEMENT_NODE && EDITOR_BLOCK_TAGS.has(node.nodeName);

    if (isBlock) {
      const prev = node.previousSibling;
      const prevIsBlock = prev && prev.nodeType === Node.ELEMENT_NODE && EDITOR_BLOCK_TAGS.has(prev.nodeName);
      const leadingNl = prev && !prevIsBlock ? 1 : 0;

      if (leadingNl) {
        if (tgt === pos) {
          const r = document.createRange();
          r.setStartBefore(node);
          r.collapse(true);
          applySelectionRange(r);
          return true;
        }
        pos += 1;
      }

      if (childNodes.length === 1 && childNodes[0].nodeName === 'BR') {
        if (tgt === pos) {
          const r = document.createRange();
          r.setStartBefore(childNodes[0]);
          r.collapse(true);
          applySelectionRange(r);
          return true;
        }
        pos += 1;
        if (tgt === pos) {
          const r = document.createRange();
          r.setStartAfter(node);
          r.collapse(true);
          applySelectionRange(r);
          return true;
        }
        return false;
      }

      for (const c of childNodes) {
        if (walk(c, false)) return true;
      }

      if (tgt === pos) {
        const last = childNodes[childNodes.length - 1];
        const r = document.createRange();
        if (last) {
          if (last.nodeType === Node.TEXT_NODE) {
            r.setStart(last, (last.nodeValue || '').length);
          } else {
            r.setStartAfter(last);
          }
        } else {
          r.setStart(node, 0);
        }
        r.collapse(true);
        applySelectionRange(r);
        return true;
      }
      pos += 1;
      if (tgt === pos) {
        const r = document.createRange();
        r.setStartAfter(node);
        r.collapse(true);
        applySelectionRange(r);
        return true;
      }
      return false;
    }

    for (const c of childNodes) {
      if (walk(c, false)) return true;
    }
    return false;
  };

  if (!walk(root, true)) {
    const r = document.createRange();
    r.selectNodeContents(root);
    r.collapse(false);
    applySelectionRange(r);
  }
}

const VariableChipInput = ({ 
  value, 
  onChange, 
  placeholder, 
  isTextarea = false,
  minH = "auto",
  hideHint = false,
  enableAiPromptChips = false,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showAiPromptHint, setShowAiPromptHint] = useState(false);
  const [isConfiguringClean, setIsConfiguringClean] = useState(false);
  const [showNestedMenu, setShowNestedMenu] = useState(false);
  
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [aiHintPosition, setAiHintPosition] = useState({ top: 0, left: 0 });
  const [nestedMenuPosition, setNestedMenuPosition] = useState({ top: 0, left: 0 });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [nestedSearchTerm, setNestedSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [nestedSelectedIndex, setNestedSelectedIndex] = useState(0);

  const [fallbackError, setFallbackError] = useState(false);

  const [columnConfirmed, setColumnConfirmed] = useState(false);
  const [cleanConfig, setCleanConfig] = useState({
    column: '',
    format: 'None',
    removeEmojis: false,
    fallback: ''
  });
  
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const aiPromptHintRef = useRef(null);
  const configRef = useRef(null);
  const nestedMenuRef = useRef(null);
  // Initialize to null (not value) so first useEffect run will populate the DOM
  const lastValueRef = useRef(null);
  const isInternalChange = useRef(false);

  const filteredVariables = AVAILABLE_VARIABLES.filter(v => 
    v.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredNestedVariables = AVAILABLE_VARIABLES.filter(v => 
    v.label.toLowerCase().includes(nestedSearchTerm.toLowerCase())
  );
  
  const showCleanAction = "clean variable".includes(searchTerm.toLowerCase());
  const menuItemsCount = filteredVariables.length + (showCleanAction ? 1 : 0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm, showMenu]);

  useEffect(() => {
    setNestedSelectedIndex(0);
  }, [nestedSearchTerm, showNestedMenu]);

  const valueToHtml = useCallback(
    (val) => {
      if (!val) return '';
      if (enableAiPromptChips) {
        const segs = parseDraftSegments(val, true);
        return segs
          .map((seg) => {
            if (seg.type === 'text') return escapeHtml(seg.content).replace(/\n/g, '<br>');
            if (seg.type === 'var') return varKeyToChipHtml(seg.content);
            if (seg.type === 'ai') {
              if (seg.closed) {
                return closedAiPromptChipHtml(seg.content);
              }
              // Unclosed //â€¦ (prompt still being written): plain editable text so the whole body
              // is not one non-editable chip until the closing // is typed.
              return `//${escapeHtml(seg.content).replace(/\n/g, '<br>')}`;
            }
            return '';
          })
          .join('');
      }
      const withChips = val.replace(/\{\{(.+?)\}\}/g, (match, content) => varKeyToChipHtml(content));
      return withChips.replace(/\n/g, '<br>');
    },
    [enableAiPromptChips]
  );

  const htmlToValue = useCallback((html) => {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;

    temp.querySelectorAll('.ai-prompt-chip').forEach((chip) => {
      const enc = chip.getAttribute('data-ai-inner') || '';
      let inner = '';
      try {
        inner = decodeURIComponent(enc);
      } catch {
        inner = '';
      }
      const closed = chip.getAttribute('data-ai-closed') === '1';
      chip.replaceWith(document.createTextNode(`//${inner}${closed ? '//' : ''}`));
    });

    // Replace variable chips with template tokens before walking
    temp.querySelectorAll('.variable-chip').forEach(chip => {
      const varKey = chip.getAttribute('data-variable');
      if (varKey) chip.replaceWith(`{{${varKey}}}`);
    });

    // Chrome's contentEditable wraps each line in <div> and represents blank lines as
    // <div><br></div>. innerText on a detached element loses all structure entirely.
    //
    // Walk the DOM tree to reconstruct the exact text with newlines:
    //   - text node:  emit its value as-is
    //   - <br>:       emit \n
    //   - block elem: emit leading \n if preceded by text/inline (closes that line),
    //                 then children, then trailing \n (ends this block's line)
    //   - <div><br></div>: Chrome's empty-line anchor â€” <br> is only for cursor
    //                      visibility, so emit leading \n (if needed) + \n for blank line
    const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'BLOCKQUOTE', 'PRE']);

    const walk = (node, isRoot = false) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return (node.nodeValue || '').replace(/\u200B/g, '');
      }
      if (node.nodeName === 'BR') return '\n';

      const childNodes = Array.from(node.childNodes);
      const isBlock = !isRoot && BLOCK_TAGS.has(node.nodeName);

      if (isBlock) {
        const prev = node.previousSibling;
        const prevIsBlock = prev && prev.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(prev.nodeName);
        // A preceding <br> already emitted its own \n to close the line, so don't
        // add another here — otherwise "text<br><div>x</div>" becomes text\n\nx
        // (a phantom blank line) instead of text\nx.
        const prevIsBr = prev && prev.nodeName === 'BR';
        // When preceded by a text/inline node, that node has no trailing \n — add one to close it
        const leadingNl = (prev && !prevIsBlock && !prevIsBr) ? '\n' : '';

        // <div><br></div>: empty-line placeholder; the <br> is not a real line break
        if (childNodes.length === 1 && childNodes[0].nodeName === 'BR') {
          return leadingNl + '\n';
        }

        return leadingNl + childNodes.map(c => walk(c)).join('') + '\n';
      }

      return childNodes.map(c => walk(c)).join('');
    };

    return walk(temp, true).replace(/\n+$/, '');
  }, []);

  const chipifyAiSectionsLive = useCallback(
    (modelStr) => {
      const el = editorRef.current;
      if (!el || document.activeElement !== el) return;
      if (!modelHasAiDelimiter(modelStr)) return;
      const nextHtml = valueToHtml(modelStr);
      if (el.innerHTML === nextHtml) return;

      const closedAiCount = parseDraftSegments(modelStr, true).filter((s) => s.type === 'ai' && s.closed).length;
      if (
        closedAiCount > 0 &&
        closedAiCount === el.querySelectorAll('.ai-prompt-chip').length &&
        htmlToValue(el.innerHTML) === modelStr
      ) {
        return;
      }

      const sel = window.getSelection();
      let off = 0;
      if (sel?.rangeCount && el.contains(sel.getRangeAt(0).startContainer)) {
        off = getModelOffsetBeforeCaret(el, sel.getRangeAt(0), htmlToValue);
      }

      el.innerHTML = nextHtml;
      lastValueRef.current = modelStr;
      setCaretToModelOffset(el, off, htmlToValue);
    },
    [htmlToValue, valueToHtml]
  );

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (editorRef.current && value !== lastValueRef.current) {
      editorRef.current.innerHTML = valueToHtml(value || '');
      lastValueRef.current = value;
    }
  }, [value, valueToHtml]);

  // Catch-up when focus leaves (e.g. tab away) â€” usually live chipify already updated the DOM.
  const handleEditorBlur = useCallback(() => {
    setShowAiPromptHint(false);
    if (!enableAiPromptChips) return;
    window.setTimeout(() => {
      if (!editorRef.current) return;
      const ae = document.activeElement;
      if (menuRef.current?.contains(ae)) return;
      if (aiPromptHintRef.current?.contains(ae)) return;
      if (configRef.current?.contains(ae)) return;
      if (nestedMenuRef.current?.contains(ae)) return;

      const latest = htmlToValue(editorRef.current.innerHTML);
      if (!modelHasAiDelimiter(latest)) return;

      const nextHtml = valueToHtml(latest);
      if (editorRef.current.innerHTML === nextHtml) return;

      const closedAi = parseDraftSegments(latest, true).filter((s) => s.type === 'ai' && s.closed).length;
      if (closedAi > 0 && closedAi === editorRef.current.querySelectorAll('.ai-prompt-chip').length) {
        return;
      }

      editorRef.current.innerHTML = nextHtml;
      lastValueRef.current = latest;
      if (latest !== value) {
        isInternalChange.current = true;
        onChange(latest);
      }
    }, 0);
  }, [enableAiPromptChips, htmlToValue, valueToHtml, value, onChange]);

  const checkSearchContext = useCallback(() => {
    if (!editorRef.current || isConfiguringClean) return;
    
    const html = editorRef.current.innerHTML;
    let newValue = htmlToValue(html);
    
    if (!newValue || newValue.trim() === '') {
      if (editorRef.current.innerHTML !== '') {
        editorRef.current.innerHTML = '';
      }
      newValue = '';
    }

    // Mask completed {{...}} pairs so lastIndexOf only finds the user's unclosed {{.
    // Same-length replacement preserves string positions for slicing from newValue.
    let masked = newValue.replace(/\{\{.+?\}\}/g, (m) => '\x01'.repeat(m.length));
    if (enableAiPromptChips) {
      masked = masked.replace(/(?<!:)\/\/[\s\S]*?(?<!:)\/\//g, (m) => '\x01'.repeat(m.length));
    }
    const lastOpenIndex = masked.lastIndexOf('{{');

    let shouldShowVariableMenu = false;
    let varSearchSlice = '';
    if (lastOpenIndex !== -1) {
      const afterOpen = newValue.slice(lastOpenIndex + 2);
      const nextNewline = afterOpen.indexOf('\n');
      const textAfterOpen = nextNewline !== -1 ? afterOpen.slice(0, nextNewline) : afterOpen;
      varSearchSlice = textAfterOpen;
      if (!textAfterOpen.includes(' ') && !textAfterOpen.includes('}}')) {
        shouldShowVariableMenu = true;
      }
    }

    if (shouldShowVariableMenu) {
      setSearchTerm(varSearchSlice);
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const menuWidth = 220;
        const padding = 16;
        let calculatedLeft = rect.left;
        if (calculatedLeft + menuWidth > window.innerWidth - padding) {
          calculatedLeft = window.innerWidth - menuWidth - padding;
        }
        setMenuPosition({ top: rect.bottom + 4, left: Math.max(padding, calculatedLeft) });
      }
      setShowMenu(true);
    } else {
      setShowMenu(false);
    }

    if (enableAiPromptChips && !shouldShowVariableMenu) {
      const segs = parseDraftSegments(newValue, true);
      const lastSeg = segs[segs.length - 1];
      if (lastSeg?.type === 'ai' && !lastSeg.closed) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.getRangeAt(0).startContainer)) {
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          const panelW = 268;
          const padding = 16;
          let left = rect.left;
          if (left + panelW > window.innerWidth - padding) {
            left = window.innerWidth - panelW - padding;
          }
          setAiHintPosition({ top: rect.bottom + 4, left: Math.max(padding, left) });
        }
        setShowAiPromptHint(true);
      } else {
        setShowAiPromptHint(false);
      }
    } else {
      setShowAiPromptHint(false);
    }

    lastValueRef.current = newValue;
    isInternalChange.current = true;
    onChange(newValue);

    if (enableAiPromptChips && modelHasAiDelimiter(newValue)) {
      chipifyAiSectionsLive(newValue);
    }
  }, [onChange, htmlToValue, isConfiguringClean, enableAiPromptChips, chipifyAiSectionsLive]);

  const insertTag = useCallback((tagKey, isPending = false) => {
    if (!editorRef.current) return;
    const currentValue = lastValueRef.current || '';
    let masked = currentValue.replace(/\{\{.+?\}\}/g, (m) => '\x01'.repeat(m.length));
    if (enableAiPromptChips) {
      masked = masked.replace(/(?<!:)\/\/[\s\S]*?(?<!:)\/\//g, (m) => '\x01'.repeat(m.length));
    }
    const lastOpenIndex = masked.lastIndexOf('{{');
    
    let newValue;
    if (lastOpenIndex !== -1) {
      const afterOpen = currentValue.slice(lastOpenIndex + 2);
      const remainder = afterOpen.split(/[\s}]/)[0] || '';
      newValue = currentValue.slice(0, lastOpenIndex) + `{{${tagKey}}}` + currentValue.slice(lastOpenIndex + 2 + remainder.length);
    } else {
      newValue = currentValue + `{{${tagKey}}}`;
    }
    
    isInternalChange.current = false; 
    onChange(newValue);
    
    if (!isPending) {
        setShowMenu(false);
        setIsConfiguringClean(false);
        setTimeout(() => {
          editorRef.current?.focus();
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }, 10);
    }
  }, [onChange, enableAiPromptChips]);

  const resetCleanState = () => {
    setCleanConfig({ column: '', format: 'None', removeEmojis: false, fallback: '' });
    setColumnConfirmed(false);
    setNestedSearchTerm('');
    setShowNestedMenu(false);
    setFallbackError(false);
  };

  const handleSaveCleanVar = () => {
    if (!cleanConfig.fallback.trim()) {
      setFallbackError(true);
      return;
    }
    const finalTag = `CLEAN|${cleanConfig.column || 'field'}|${cleanConfig.format}|${cleanConfig.removeEmojis}|${cleanConfig.fallback}`;
    const newValue = lastValueRef.current.replace('{{CLEAN_PENDING}}', `{{${finalTag}}}`);
    isInternalChange.current = false;
    onChange(newValue);
    setIsConfiguringClean(false);
    resetCleanState();
  };

  const cancelCleanVar = useCallback(() => {
    const newValue = (lastValueRef.current || '').replace('{{CLEAN_PENDING}}', '');
    isInternalChange.current = false;
    onChange(newValue);
    setIsConfiguringClean(false);
    resetCleanState();
  }, [onChange]);

  const selectNestedVariable = (variable) => {
    setCleanConfig(prev => ({ ...prev, column: variable.label }));
    setColumnConfirmed(true);
    setShowNestedMenu(false);
    setNestedSearchTerm('');
  };

  const handleColumnInput = (e) => {
    const val = e.target.value;
    setCleanConfig(prev => ({ ...prev, column: val }));
    if (val.includes('{{')) {
      if (configRef.current) {
        const inputRect = e.target.getBoundingClientRect();
        const panelRect = configRef.current.getBoundingClientRect();
        
        const nestedWidth = 220;
        let calculatedLeft = panelRect.left - nestedWidth - 10;
        
        if (calculatedLeft < 10) {
            calculatedLeft = panelRect.right + 10;
        }

        setNestedMenuPosition({ top: inputRect.top, left: calculatedLeft });
        setShowNestedMenu(true);
        const lastOpenIndex = val.lastIndexOf('{{');
        setNestedSearchTerm(lastOpenIndex !== -1 ? val.slice(lastOpenIndex + 2) : '');
      }
    } else if (showNestedMenu) {
      const lastOpenIndex = val.lastIndexOf('{{');
      if (lastOpenIndex !== -1) {
        setNestedSearchTerm(val.slice(lastOpenIndex + 2));
      } else {
        setShowNestedMenu(false);
      }
    }
  };

  const handleNestedKeyDown = (e) => {
    if (showNestedMenu && filteredNestedVariables.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setNestedSelectedIndex(prev => (prev + 1) % filteredNestedVariables.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setNestedSelectedIndex(prev => (prev - 1 + filteredNestedVariables.length) % filteredNestedVariables.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        selectNestedVariable(filteredNestedVariables[nestedSelectedIndex]);
      } else if (e.key === 'Escape') {
        setShowNestedMenu(false);
      }
    }
  };

  const handleEditorKeyDown = (e) => {
    if (showMenu && menuItemsCount > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % menuItemsCount);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + menuItemsCount) % menuItemsCount);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const isCleanSelected = showCleanAction && selectedIndex === 0;
        if (isCleanSelected) {
          // INSERT PENDING CHIP FIRST
          insertTag('CLEAN_PENDING', true);
          setIsConfiguringClean(true);
          setShowMenu(false);
          
          // PREMIUM FIX: Find actual chip position to anchor dropdown exactly below it
          setTimeout(() => {
            const pendingChip = editorRef.current?.querySelector('[data-variable="CLEAN_PENDING"]');
            if (pendingChip) {
              const chipRect = pendingChip.getBoundingClientRect();
              const configWidth = 320;
              const padding = 16;
              let configLeft = chipRect.left;
              
              if (configLeft + configWidth > window.innerWidth - padding) {
                configLeft = window.innerWidth - configWidth - padding;
              }

              setMenuPosition({ 
                top: chipRect.bottom + 8, // Force exactly below the chip line
                left: Math.max(padding, configLeft) 
              });
            }
          }, 0);
        } else {
          const varIdx = showCleanAction ? selectedIndex - 1 : selectedIndex;
          insertTag(filteredVariables[varIdx].key);
        }
      }
    }
    if (e.key === 'Escape') {
      if (isConfiguringClean) cancelCleanVar();
      setShowMenu(false);
      setShowAiPromptHint(false);
    }
  };

  const handleClick = useCallback((e) => {
    const aiCloseBtn = e.target.closest('.ai-prompt-chip-close');
    if (aiCloseBtn) {
      e.preventDefault();
      e.stopPropagation();
      const chip = aiCloseBtn.closest('.ai-prompt-chip');
      chip?.remove();
      if (editorRef.current) {
        const newValue = htmlToValue(editorRef.current.innerHTML);
        lastValueRef.current = newValue;
        isInternalChange.current = true;
        onChange(newValue);
      }
      return;
    }
    const aiChipEdit = e.target.closest('.ai-prompt-chip');
    if (aiChipEdit && !e.target.closest('.ai-prompt-chip-close')) {
      e.preventDefault();
      e.stopPropagation();
      if (!editorRef.current) return;
      const enc = aiChipEdit.getAttribute('data-ai-inner') || '';
      let inner = '';
      try {
        inner = decodeURIComponent(enc);
      } catch {
        inner = '';
      }
      // Drop closing // so the segment is "open" again: user edits freely, then types // to finish.
      const plain = `//${inner}`;

      const rBefore = document.createRange();
      rBefore.setStartBefore(aiChipEdit);
      rBefore.collapse(true);
      const chipStart = getModelOffsetBeforeCaret(editorRef.current, rBefore, htmlToValue);

      aiChipEdit.replaceWith(document.createTextNode(plain));

      const newValue = htmlToValue(editorRef.current.innerHTML);
      lastValueRef.current = newValue;
      isInternalChange.current = true;
      onChange(newValue);

      requestAnimationFrame(() => {
        if (!editorRef.current) return;
        setCaretToModelOffset(editorRef.current, chipStart + 2 + inner.length, htmlToValue);
        editorRef.current.focus();
      });
      return;
    }
    const closeBtn = e.target.closest('.chip-close');
    if (closeBtn) {
      e.preventDefault(); e.stopPropagation();
      const chip = closeBtn.closest('.variable-chip');
      if (chip) {
        const varKey = chip.getAttribute('data-variable');
        chip.remove();
        if (editorRef.current) {
          const newValue = htmlToValue(editorRef.current.innerHTML);
          lastValueRef.current = newValue;
          isInternalChange.current = true;
          onChange(newValue);
          if (varKey === 'CLEAN_PENDING') {
            setIsConfiguringClean(false);
            resetCleanState();
          }
        }
      }
    } else {
      checkSearchContext();
    }
  }, [htmlToValue, onChange, checkSearchContext]);

  // Clamp config panel inside viewport after it renders.
  // useLayoutEffect with only isConfiguringClean as dep so it runs once on open
  // and never loops. clientWidth excludes the browser scrollbar (window.innerWidth doesn't).
  useLayoutEffect(() => {
    if (!isConfiguringClean) return;
    const raf = requestAnimationFrame(() => {
      if (!configRef.current) return;
      const rect = configRef.current.getBoundingClientRect();
      const availW = document.documentElement.clientWidth;
      const availH = document.documentElement.clientHeight;
      const padding = 16;
      setMenuPosition(prev => {
        let { top, left } = prev;
        if (rect.right > availW - padding) left = availW - rect.width - padding;
        if (rect.bottom > availH - padding) top = availH - rect.height - padding;
        return { top: Math.max(padding, top), left: Math.max(padding, left) };
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isConfiguringClean]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isConfiguringClean) {
        const isInsideConfig = configRef.current && configRef.current.contains(event.target);
        const isInsideNested = nestedMenuRef.current && nestedMenuRef.current.contains(event.target);
        if (!isInsideConfig && !isInsideNested) cancelCleanVar();
      }
      if (showMenu) {
        const isInsideMenu = menuRef.current && menuRef.current.contains(event.target);
        const isInsideContainer = containerRef.current && containerRef.current.contains(event.target);
        if (!isInsideMenu && !isInsideContainer) setShowMenu(false);
      }
      if (showAiPromptHint) {
        const inHint = aiPromptHintRef.current?.contains(event.target);
        const inContainer = containerRef.current?.contains(event.target);
        if (!inHint && !inContainer) setShowAiPromptHint(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu, showAiPromptHint, isConfiguringClean, cancelCleanVar]);

  return (
    <Box position="relative" ref={containerRef}>
      <style>{chipStyles}</style>
      <Box
        bg="#111111"
        border="1px solid"
        borderColor="rgba(255,255,255,0.12)"
        borderRadius="lg"
        p={3}
        minH={minH}
        cursor="text"
        onClick={() => editorRef.current?.focus()}
        _focusWithin={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
      >
        <Box
          ref={editorRef} contentEditable suppressContentEditableWarning
          onInput={checkSearchContext} onKeyDown={handleEditorKeyDown}
          onBlur={handleEditorBlur}
          onKeyUp={(e) => ['ArrowLeft', 'ArrowRight'].includes(e.key) && checkSearchContext()}
          onClick={handleClick} color="#fafafa" fontSize="sm" minH={isTextarea ? minH : "auto"}
          outline="none" whiteSpace="pre-wrap" wordBreak="break-word"
          sx={{ 
            '&:empty:before': { 
              content: `"${placeholder}"`, 
              color: 'rgba(255,255,255,0.3)',
              pointerEvents: 'none'
            } 
          }}
        />
        {!hideHint && (
          <Text color="rgba(255,255,255,0.3)" fontSize="xs" mt={2}>
            Type {'{{...}}'} for variables, type {'//...//'} for AI prompt injection.
          </Text>
        )}
      </Box>

      {showMenu && (
        <Portal>
          <Box
            ref={menuRef}
            position="fixed"
            top={`${menuPosition.top}px`}
            left={`${menuPosition.left}px`}
            bg="#0f0f0f"
            border="1px solid"
            borderColor="rgba(255,255,255,0.10)"
            borderRadius="lg"
            zIndex={2000}
            minW="180px"
            maxH="240px"
            overflowY="auto"
            py={1}
            px={1}
            boxShadow="0 4px 24px rgba(0,0,0,0.5)"
            sx={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {showCleanAction && (
              <Box
                mx={0}
                px={1.5}
                py={1.5}
                cursor="pointer"
                borderRadius="md"
                bg={selectedIndex === 0 ? 'rgba(255,255,255,0.08)' : 'transparent'}
                _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                onClick={() => {
                  const padding = 16;
                  let configLeft = menuPosition.left;
                  if (configLeft + 320 > window.innerWidth - padding) {
                    configLeft = window.innerWidth - 320 - padding;
                  }
                  setMenuPosition(prev => ({ ...prev, left: Math.max(padding, configLeft) }));
                  insertTag('CLEAN_PENDING', true);
                  setIsConfiguringClean(true);
                  setShowMenu(false);
                }}
              >
                <HStack color="rgba(255,255,255,0.6)" spacing={2}>
                  <LuEraser size={14} />
                  <Text fontSize="sm">Clean variable</Text>
                </HStack>
              </Box>
            )}
            <Box px={1.5} py={1}>
              <Text color="rgba(255,255,255,0.3)" fontSize="2xs" fontWeight="medium" textTransform="uppercase" letterSpacing="wider" sx={{ fontSize: '10px' }}>
                Variables
              </Text>
            </Box>
            {filteredVariables.map((variable, index) => {
              const actualIdx = showCleanAction ? index + 1 : index;
              return (
                <Box
                  key={variable.key}
                  mx={0}
                  px={1.5}
                  py={1.5}
                  cursor="pointer"
                  borderRadius="md"
                  bg={actualIdx === selectedIndex ? 'rgba(255,255,255,0.08)' : 'transparent'}
                  _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                  onClick={() => insertTag(variable.key)}
                >
                  <HStack spacing={2}>
                    <Box as={LuType} color="rgba(255,255,255,0.3)" fontSize="sm" />
                    <VStack align="start" spacing={0}>
                      <Text color="#fafafa" fontSize="sm">{variable.label}</Text>
                      <Text color="rgba(255,255,255,0.3)" fontSize="2xs">{`{{${variable.key}}}`}</Text>
                    </VStack>
                  </HStack>
                </Box>
              );
            })}
          </Box>
        </Portal>
      )}

      {showAiPromptHint && enableAiPromptChips && (
        <Portal>
          <Box
            ref={aiPromptHintRef}
            position="fixed"
            top={`${aiHintPosition.top}px`}
            left={`${aiHintPosition.left}px`}
            zIndex={1999}
            minW="248px"
            maxW="300px"
            bg="#0f0f0f"
            border="1px solid"
            borderColor="rgba(74, 222, 128, 0.3)"
            borderRadius="lg"
            boxShadow="0 4px 24px rgba(0,0,0,0.5)"
            overflow="hidden"
            onMouseDown={(e) => e.preventDefault()}
          >
            <HStack
              spacing={2}
              px={3}
              py={2}
              bg="rgba(34, 197, 94, 0.08)"
              borderBottom="1px solid"
              borderColor="rgba(74, 222, 128, 0.15)"
            >
              <Box as={LuSparkles} color="#4ade80" fontSize="16px" />
              <Text fontSize="sm" fontWeight="semibold" color="#4ade80">
                AI prompt
              </Text>
            </HStack>
            <VStack align="stretch" spacing={2} px={3} py={2.5}>
              <Text fontSize="2xs" fontWeight="bold" color="rgba(255,255,255,0.3)" textTransform="uppercase" letterSpacing="wider">
                Write an instruction
              </Text>
              <Text fontSize="sm" color="rgba(255,255,255,0.6)" lineHeight="short">
                Describe what the model should generate in this section. When you are finished, close with another{' '}
                <Text as="span" color="#4ade80" fontFamily="mono" fontSize="sm">
                  {'//'}
                </Text>
                {' '}â€” like ending a comment.
              </Text>
              <Text fontSize="2xs" color="rgba(255,255,255,0.3)" fontFamily="mono">
                {'// short personalized opener //'}
              </Text>
            </VStack>
          </Box>
        </Portal>
      )}

      {isConfiguringClean && (
        <Portal>
          <Box
            ref={configRef} position="fixed" top={`${menuPosition.top}px`} left={`${menuPosition.left}px`}
            bg="#0f0f0f" border="1px solid" borderColor="rgba(255,255,255,0.12)" borderRadius="lg"
            zIndex={2001} w="320px" boxShadow="2xl" p={4} color="#fafafa"
          >
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" fontWeight="bold">Clean variable</Text>
              <FormControl>
                <FormLabel fontSize="xs" color="#a3a3a3" mb={1}>Column to use if present</FormLabel>
                {columnConfirmed ? (
                  <Box
                    bg="#111111" border="1px solid" borderColor="rgba(255,255,255,0.12)"
                    borderRadius="md" px={2} h="32px" display="flex" alignItems="center"
                  >
                    <Box as="span" className="variable-chip" m={0} h="20px" bg="rgba(255, 255, 255, 0.12)">
                      <Box as="span" className="chip-icon" mr="4px">T</Box>
                      <Box as="span" className="chip-text" fontSize="12px">{cleanConfig.column}</Box>
                      <Box
                        as="span" className="chip-close" ml="6px" mr="0px"
                        onClick={() => { setCleanConfig(prev => ({ ...prev, column: '' })); setColumnConfirmed(false); }}
                      >
                        ×
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Input 
                    placeholder="{{first_name}}" size="sm" bg="#111111" border="1px solid rgba(255,255,255,0.12)"
                    color="#fafafa" _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                    value={cleanConfig.column} onChange={handleColumnInput} onKeyDown={handleNestedKeyDown}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }} autoFocus
                  />
                )}
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="#a3a3a3" mb={1}>Variable formatting</FormLabel>
                <Select 
                  size="sm" bg="#111111" border="1px solid rgba(255,255,255,0.12)" color="#fafafa" icon={<LuChevronDown />}
                  value={cleanConfig.format} onChange={(e) => setCleanConfig({...cleanConfig, format: e.target.value})}
                  _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                >
                  <option value="None" style={{background: '#0f0f0f'}}>None</option>
                  <option value="Title Case" style={{background: '#0f0f0f'}}>Title Case</option>
                  <option value="lower case" style={{background: '#0f0f0f'}}>lower case</option>
                  <option value="Sentence case" style={{background: '#0f0f0f'}}>Sentence case</option>
                  <option value="UPPER CASE" style={{background: '#0f0f0f'}}>UPPER CASE</option>
                </Select>
              </FormControl>
              <Checkbox 
                size="sm" colorScheme="whiteAlpha"
                isChecked={cleanConfig.removeEmojis} onChange={(e) => setCleanConfig({...cleanConfig, removeEmojis: e.target.checked})}
              >
                <Text fontSize="xs" color="rgba(255,255,255,0.6)">Remove emojis and special characters</Text>
              </Checkbox>
              <FormControl isInvalid={fallbackError}>
                <FormLabel fontSize="xs" color="#a3a3a3" mb={0}>Fallback</FormLabel>
                <Text fontSize="10px" color="rgba(255,255,255,0.3)" mb={1}>Simple text to display if variable is empty</Text>
                <Input 
                  placeholder="Esteemed friend" size="sm" bg="#111111"
                  color="#fafafa" _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                  border="1px solid" 
                  borderColor={fallbackError ? "rgba(239,68,68,0.6)" : "rgba(255,255,255,0.12)"}
                  value={cleanConfig.fallback} 
                  onChange={(e) => {
                    setCleanConfig({...cleanConfig, fallback: e.target.value});
                    if (e.target.value.trim()) setFallbackError(false);
                  }}
                  _focus={{ borderColor: fallbackError ? 'rgba(239,68,68,0.8)' : 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                />
                {fallbackError && (
                  <Text color="rgba(239,68,68,0.85)" fontSize="10px" mt={1}>
                    At least one character is required
                  </Text>
                )}
              </FormControl>
              <HStack spacing={2} pt={2}>
                <Button flex={1} variant="ghost" size="sm" color="#a3a3a3" borderRadius="full" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} onClick={cancelCleanVar}>Cancel</Button>
                <Button flex={1} bg="#fafafa" color="#0a0a0a" borderRadius="full" fontWeight="600" size="sm" _hover={{ opacity: 0.85 }} _focus={{ boxShadow: 'none' }} onClick={handleSaveCleanVar}>Save</Button>
              </HStack>
            </VStack>
          </Box>
        </Portal>
      )}

      {showNestedMenu && isConfiguringClean && (
        <Portal>
          <Box
            ref={nestedMenuRef} position="fixed" top={`${nestedMenuPosition.top}px`} left={`${nestedMenuPosition.left}px`}
            bg="#0f0f0f" border="1px solid rgba(255,255,255,0.10)" borderRadius="md"
            zIndex={2005} minW="220px" maxH="250px" overflowY="auto" boxShadow="xl"
            sx={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <VStack spacing={0} align="stretch">
              <Box px={3} py={2} borderBottom="1px solid rgba(255,255,255,0.08)">
                <Text color="rgba(255,255,255,0.3)" fontSize="xs" fontWeight="bold">SELECT VARIABLE</Text>
              </Box>
              {filteredNestedVariables.map((variable, index) => (
                <Box
                  key={variable.key} px={3} py={2} cursor="pointer"
                  bg={index === nestedSelectedIndex ? 'rgba(255,255,255,0.08)' : 'transparent'}
                  _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                  onClick={() => selectNestedVariable(variable)}
                >
                  <HStack>
                    <Box as={LuType} color="rgba(255,255,255,0.3)" fontSize="sm" />
                    <Text color="#fafafa" fontSize="sm">{variable.label}</Text>
                  </HStack>
                  <Text color="rgba(255,255,255,0.3)" fontSize="xs">{`{{${variable.key}}}`}</Text>
                </Box>
              ))}
            </VStack>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default VariableChipInput;
export { AVAILABLE_VARIABLES };

